import type { JSX } from 'react';

import { Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

export type ReachMoreSection = {
	id: string;
	title: string;
	body: string;
};

/** Keyframes for bar fill (40%→100%); scoped here so we don't touch global app CSS */
const BAR_FILL_KEYFRAMES = '@keyframes reach-more-fill { from { width: 40%; } to { width: 100%; } }';

type FeatureListProps = {
	sections: readonly ReachMoreSection[];
	selectedIndex: number;
	onSelect: (index: number) => void;
	/** Duration of the bar fill animation (ms) */
	fillDurationMs?: number;
	/** When false, bar stays at 40% (no animation); true after user scrolls */
	enableBarAnimation?: boolean;
};

export function FeatureList({
	sections,
	selectedIndex,
	onSelect,
	fillDurationMs = 4000,
	enableBarAnimation = true,
}: FeatureListProps): JSX.Element {
	return (
		<div className="flex flex-col">
			<style>{BAR_FILL_KEYFRAMES}</style>
			{sections.map((section, index) => {
				const isSelected = index === selectedIndex;
				return (
					<div key={section.id} className={cn({ 'mt-6 pt-6': index > 0 })}>
						{/* Bar above each option: selected has blue bar that fills 40%→100% via CSS animation */}
						<div className="mb-3 -ml-4 flex h-0.5 w-[calc(100%+1rem)] overflow-hidden" aria-hidden role="presentation">
							{isSelected ? (
								<>
									<div
										className="w-2/5 min-w-0 bg-[var(--Blue-500,#3b82f6)]"
										style={
											enableBarAnimation
												? { animation: `reach-more-fill ${fillDurationMs}ms linear forwards` }
												: undefined
										}
									/>
									<div className="bg-fill-weaker flex-1" />
								</>
							) : (
								<div className="bg-fill-weaker h-full w-full" />
							)}
						</div>
						<button
							type="button"
							className="group flex w-full flex-col gap-2 border-0 bg-transparent p-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
							onClick={() => onSelect(index)}
							aria-pressed={isSelected}
							aria-label={`${section.title}. ${isSelected ? 'Selected.' : 'Select to view.'}`}
						>
							<Text
								as="span"
								variant="body"
								size="large"
								fontWeight="bold"
								className={cn({ 'text-typography-weak': !isSelected })}
							>
								{section.title}
							</Text>
							<Text variant="body" size="medium" className={cn({ 'text-typography-weak': !isSelected })}>
								{section.body}
							</Text>
						</button>
					</div>
				);
			})}
		</div>
	);
}
